import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useDevMode } from '../contexts/DevModeContext';
import { Clock, Play, Square, X, Check, Edit2, Trash2, Save } from 'lucide-react';
import axios from 'axios';

const TimeTracker = () => {
  const { user } = useAuth();
  const { devMode, dummyData } = useDevMode();
  const [isOpen, setIsOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState('');
  const [tasks, setTasks] = useState([]);
  const [runningTimer, setRunningTimer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [todayHistory, setTodayHistory] = useState([]);
  const [todayHoursFromAPI, setTodayHoursFromAPI] = useState([]);
  const [editingHours, setEditingHours] = useState(null);
  const [editHoursValue, setEditHoursValue] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const timerIntervalRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

  // Load timer state from localStorage on mount
  useEffect(() => {
    const savedTimer = localStorage.getItem('runningTimer');
    if (savedTimer) {
      try {
        const timer = JSON.parse(savedTimer);
        const startTime = new Date(timer.startTime);
        const now = new Date();
        const elapsed = Math.floor((now - startTime) / 1000);
        
        setRunningTimer(timer);
        setElapsedSeconds(elapsed);
      } catch (error) {
        console.error('Error loading saved timer:', error);
        localStorage.removeItem('runningTimer');
      }
    }
  }, []);

  // Save timer state to localStorage whenever it changes
  useEffect(() => {
    if (runningTimer) {
      localStorage.setItem('runningTimer', JSON.stringify(runningTimer));
    } else {
      localStorage.removeItem('runningTimer');
    }
  }, [runningTimer]);

  // Fetch user's tasks and today's hours
  useEffect(() => {
    if (isOpen && user) {
      fetchTasks();
      loadTodayHistory();
      fetchTodayHours();
    }
  }, [isOpen, user]);

  // Timer tick
  useEffect(() => {
    if (runningTimer) {
      timerIntervalRef.current = setInterval(() => {
        setElapsedSeconds(prev => prev + 1);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [runningTimer]);

  const fetchTasks = async () => {
    try {
      if (devMode) {
        // Use dummy data
        setTasks(dummyData.tasks.filter(t => !t.field_50_raw)); // not completed
      } else {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BACKEND_URL}/api/tasks/user/${user.id}?rows_per_page=1000`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        console.log('Tasks API response:', response.data);
        // Fix: API returns 'records' not 'tasks'
        const tasksData = response.data.records || response.data.tasks || [];
        setTasks(tasksData.filter(t => !t.field_50_raw));
        console.log(`Loaded ${tasksData.length} tasks, ${tasksData.filter(t => !t.field_50_raw).length} incomplete`);
      }
    } catch (error) {
      console.error('Error fetching tasks:', error);
      setTasks([]); // Set empty array on error
    }
  };

  const loadTodayHistory = () => {
    const saved = localStorage.getItem('timeTrackerHistory');
    if (saved) {
      const history = JSON.parse(saved);
      const today = new Date().toDateString();
      const todayEntries = history.filter(h => new Date(h.date).toDateString() === today);
      setTodayHistory(todayEntries);
    }
  };

  const saveToHistory = (entry) => {
    const saved = localStorage.getItem('timeTrackerHistory');
    const history = saved ? JSON.parse(saved) : [];
    history.push(entry);
    localStorage.setItem('timeTrackerHistory', JSON.stringify(history));
    loadTodayHistory();
  };

  const fetchTodayHours = async () => {
    if (!user?.id) return;
    
    try {
      if (!devMode) {
        const token = localStorage.getItem('token');
        const response = await axios.get(
          `${BACKEND_URL}/api/hours/user/${user.id}`,
          { 
            headers: { Authorization: `Bearer ${token}` },
            params: { rows_per_page: 100 }
          }
        );
        
        // Filter for today's hours
        const today = new Date().toDateString();
        const todayHours = (response.data.records || []).filter(record => {
          const recordDate = record.field_37_raw || record.field_37;
          if (!recordDate) return false;
          return new Date(recordDate).toDateString() === today;
        });
        
        setTodayHoursFromAPI(todayHours);
      }
    } catch (error) {
      console.error('Error fetching today hours:', error);
    }
  };

  const deleteHoursRecord = async (hoursId) => {
    if (!confirm('Delete this hours entry?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/hours/${hoursId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Refresh the list
      await fetchTodayHours();
      alert('Hours entry deleted successfully');
    } catch (error) {
      console.error('Error deleting hours:', error);
      alert('Failed to delete hours entry');
    }
  };

  const startEditHours = (hoursRecord) => {
    setEditingHours(hoursRecord.id);
    setEditHoursValue(hoursRecord.field_36_raw || hoursRecord.field_36 || '');
    setEditNotes(hoursRecord.field_38_raw || hoursRecord.field_38 || '');
  };

  const cancelEditHours = () => {
    setEditingHours(null);
    setEditHoursValue('');
    setEditNotes('');
  };

  const saveEditHours = async (hoursId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/hours/${hoursId}`,
        {
          field_36: parseFloat(editHoursValue),
          field_38: editNotes
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Reset editing state
      setEditingHours(null);
      setEditHoursValue('');
      setEditNotes('');
      
      // Refresh the list
      await fetchTodayHours();
      alert('Hours entry updated successfully');
    } catch (error) {
      console.error('Error updating hours:', error);
      alert('Failed to update hours entry');
    }
  };

  const roundToQuarterHour = (totalSeconds) => {
    const totalMinutes = totalSeconds / 60;
    const roundedMinutes = Math.ceil(totalMinutes / 15) * 15;
    return roundedMinutes / 60; // return in hours
  };

  const formatTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    if (m === 0) return `${h}h`;
    return `${h}h${m > 0 ? m : ''}`;
  };

  const startTimer = (taskId) => {
    if (!taskId) return;

    // Stop any running timer first
    if (runningTimer) {
      stopTimer();
    }

    const task = tasks.find(t => t.id === taskId);
    setRunningTimer({
      taskId,
      taskName: task?.field_43_raw || 'Unknown Task',
      startTime: new Date(),
    });
    setElapsedSeconds(0);
    setSelectedTask('');
  };

  const stopTimer = async () => {
    if (!runningTimer) return;

    const roundedHours = roundToQuarterHour(elapsedSeconds);
    const now = new Date();

    // Save to history
    const historyEntry = {
      taskId: runningTimer.taskId,
      taskName: runningTimer.taskName,
      date: now.toISOString(),
      hours: roundedHours,
      rawSeconds: elapsedSeconds
    };
    saveToHistory(historyEntry);

    // Register hours via API
    try {
      if (!devMode) {
        const token = localStorage.getItem('token');
        const dateStr = now.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
        
        await axios.post(
          `${BACKEND_URL}/api/hours`,
          {
            field_182: [runningTimer.taskId],  // Task connection (CORRECT FIELD!)
            field_36: roundedHours,            // Hours worked (field_36 not field_112)
            field_37: now.toISOString().split('T')[0], // Date (field_37 not field_113)
            field_38: `Auto-tracked via timer (${formatTime(elapsedSeconds)})`, // Notes (field_38 not field_114)
            field_62: [user.id]                // User connection (must be array)
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      // Refresh today's hours list
      await fetchTodayHours();
    } catch (error) {
      console.error('Error registering hours:', error);
      alert('Failed to register hours. Entry saved locally.');
    }

    // Reset timer
    setRunningTimer(null);
    setElapsedSeconds(0);
  };

  const handleStartNewTask = () => {
    if (runningTimer) {
      stopTimer();
    }
    // Show task selection
    setSelectedTask('');
  };

  return (
    <>
      {/* Clock Icon Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`p-2 rounded-lg transition-colors relative ${
          runningTimer 
            ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 animate-pulse' 
            : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
        }`}
        title="Time Tracker"
      >
        <Clock className="w-5 h-5" />
        {runningTimer && (
          <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full animate-ping"></span>
        )}
      </button>

      {/* Drawer */}
      {isOpen && (
        <>
          {/* Backdrop */}
          <div 
            className="fixed inset-0 bg-black/20 z-40"
            onClick={() => setIsOpen(false)}
          />

          {/* Drawer Content */}
          <div className="fixed top-16 right-4 w-96 bg-white dark:bg-gray-800 rounded-lg shadow-xl z-50 border border-gray-200 dark:border-gray-700 max-h-[80vh] overflow-y-auto">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between sticky top-0 bg-white dark:bg-gray-800">
              <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Time Tracker
              </h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <X className="w-4 h-4 text-gray-500" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* Running Timer Display */}
              {runningTimer && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-green-800 dark:text-green-300">
                      Currently Tracking
                    </span>
                    <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      Active
                    </span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white mb-2">
                    {runningTimer.taskName}
                  </p>
                  <div className="text-3xl font-mono font-bold text-green-700 dark:text-green-400 mb-2">
                    {formatTime(elapsedSeconds)}
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 mb-3">
                    Will be registered as: {formatHours(roundToQuarterHour(elapsedSeconds))}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={stopTimer}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                    >
                      <Square className="w-4 h-4" />
                      Stop & Register
                    </button>
                    <button
                      onClick={handleStartNewTask}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Switch Task
                    </button>
                  </div>
                </div>
              )}

              {/* Task Selection */}
              {!runningTimer && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Select Task to Track
                  </label>
                  <select
                    value={selectedTask}
                    onChange={(e) => setSelectedTask(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white mb-3"
                  >
                    <option value="">Choose a task...</option>
                    {tasks.map(task => {
                      const projectName = task.field_48_raw?.[0]?.identifier || 'No Project';
                      return (
                        <option key={task.id} value={task.id}>
                          [{projectName}] {task.field_43_raw}
                        </option>
                      );
                    })}
                  </select>
                  <button
                    onClick={() => startTimer(selectedTask)}
                    disabled={!selectedTask}
                    className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <Play className="w-4 h-4" />
                    Start Timer
                  </button>
                </div>
              )}

              {/* Today's Hours from API */}
              {todayHoursFromAPI.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                    <Check className="w-4 h-4 text-green-600" />
                    Today's Hours Logged
                  </h4>
                  <div className="space-y-2">
                    {todayHoursFromAPI.map((record) => {
                      const taskConnection = record.field_182_raw || [];
                      const taskName = taskConnection.length > 0 ? taskConnection[0].identifier : 'Unknown Task';
                      const hours = record.field_36_raw || record.field_36 || 0;
                      const notes = record.field_38_raw || record.field_38 || '';
                      const isEditing = editingHours === record.id;
                      
                      return (
                        <div
                          key={record.id}
                          className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-3 border border-gray-200 dark:border-gray-600"
                        >
                          {isEditing ? (
                            // Edit Mode
                            <div className="space-y-2">
                              <div className="flex-1">
                                <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                                  {taskName}
                                </p>
                                <div className="flex gap-2 items-center mb-2">
                                  <label className="text-xs text-gray-600 dark:text-gray-400">Hours:</label>
                                  <input
                                    type="number"
                                    step="0.25"
                                    value={editHoursValue}
                                    onChange={(e) => setEditHoursValue(e.target.value)}
                                    className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                  />
                                </div>
                                <div className="mb-2">
                                  <label className="text-xs text-gray-600 dark:text-gray-400 block mb-1">Notes:</label>
                                  <textarea
                                    value={editNotes}
                                    onChange={(e) => setEditNotes(e.target.value)}
                                    rows={2}
                                    className="w-full px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                                  />
                                </div>
                              </div>
                              <div className="flex gap-2 justify-end">
                                <button
                                  onClick={cancelEditHours}
                                  className="px-3 py-1 text-xs bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-300 dark:hover:bg-gray-500"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={() => saveEditHours(record.id)}
                                  className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 flex items-center gap-1"
                                >
                                  <Save className="w-3 h-3" />
                                  Save
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                                  {taskName}
                                </p>
                                {notes && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {notes}
                                  </p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <p className="text-sm font-semibold text-green-600 dark:text-green-400">
                                  {formatHours(hours)}
                                </p>
                                <button
                                  onClick={() => startEditHours(record)}
                                  className="p-1 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded text-blue-600 dark:text-blue-400"
                                  title="Edit"
                                >
                                  <Edit2 className="w-3 h-3" />
                                </button>
                                <button
                                  onClick={() => deleteHoursRecord(record.id)}
                                  className="p-1 hover:bg-red-100 dark:hover:bg-red-900/30 rounded text-red-600 dark:text-red-400"
                                  title="Delete"
                                >
                                  <Trash2 className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Total Today
                      </span>
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">
                        {formatHours(todayHoursFromAPI.reduce((sum, r) => sum + (r.field_36_raw || r.field_36 || 0), 0))}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default TimeTracker;
